import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ArrowRight, Wheat, Droplet, MapPin } from 'lucide-react';

const COMPARISON_FIELDS = [
  { field: 'total_land_holding_size_acres', label: 'Total Land (Acres)', icon: MapPin },
  { field: 'land_area_hectares', label: 'Land (Hectares)', icon: MapPin },
  { field: 'primary_crop_type', label: 'Primary Crop', icon: Wheat },
  { field: 'crop_diversification_index', label: 'Crop Diversification', icon: Wheat },
  { field: 'annual_agricultural_income', label: 'Agricultural Income', icon: null, format: 'currency' },
];

const CultivationChanges = ({ farmer, assessment, previousAssessment }) => {
  const prevSnapshot = previousAssessment?.farmer_profile_snapshot || {};
  const changedFields = new Set((assessment?.changed_fields || []).filter(cf => cf.section === 'agricultural').map(cf => cf.field));

  const formatVal = (val, format) => {
    if (val === null || val === undefined) return 'N/A';
    if (format === 'currency') return `Rs. ${Number(val).toLocaleString()}`;
    if (typeof val === 'number') return val.toLocaleString();
    return String(val);
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="cultivation-changes-title">
          Cultivation & Crop Plan Changes
        </h3>
        <p className="text-sm text-gray-500">
          Review and document changes in cultivation area, crop plan, and agricultural economics compared to previous cycle.
        </p>
      </div>

      {/* Comparison Table */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-4 gap-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wide pb-2 border-b border-gray-200 mb-2">
            <span>Field</span>
            <span className="text-right">Previous</span>
            <span className="text-center"></span>
            <span>Current</span>
          </div>
          {COMPARISON_FIELDS.map(({ field, label, format }) => {
            const prevVal = prevSnapshot[field];
            const currVal = farmer?.[field];
            const isChanged = changedFields.has(field);

            return (
              <div key={field} className={`grid grid-cols-4 gap-2 items-center py-2 border-b border-gray-50 ${isChanged ? 'bg-amber-50/50 -mx-2 px-2 rounded' : ''}`}>
                <span className="text-xs text-gray-700 font-medium flex items-center gap-1">
                  {isChanged && <AlertTriangle className="h-3 w-3 text-amber-500" />}
                  {label}
                </span>
                <span className={`text-xs text-right ${isChanged ? 'text-gray-400 line-through' : 'text-gray-600'}`}>
                  {formatVal(prevVal, format)}
                </span>
                <div className="flex justify-center">
                  {isChanged && <ArrowRight className="h-3 w-3 text-amber-500" />}
                </div>
                <span className={`text-xs font-medium ${isChanged ? 'text-amber-700' : 'text-gray-800'}`}>
                  {formatVal(currVal, format)}
                </span>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Land Parcels Comparison */}
      <Card>
        <CardContent className="pt-4">
          <div className="font-semibold text-xs text-gray-500 uppercase tracking-wide mb-3">Land Parcels</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] text-gray-400 uppercase mb-1.5">Previous ({(prevSnapshot.land_parcels || []).length} parcels)</div>
              {(prevSnapshot.land_parcels || []).map((p, i) => (
                <div key={i} className="text-xs text-gray-600 py-1 border-b border-gray-100">
                  {p.village || 'N/A'} &mdash; {p.area} {p.area_unit} &mdash; {p.irrigation_status}
                </div>
              ))}
              {(!prevSnapshot.land_parcels || prevSnapshot.land_parcels.length === 0) && (
                <div className="text-xs text-gray-400">No parcels in snapshot</div>
              )}
            </div>
            <div>
              <div className="text-[10px] text-amber-600 uppercase mb-1.5">Current ({(farmer?.land_parcels || []).length} parcels)</div>
              {(farmer?.land_parcels || []).map((p, i) => (
                <div key={i} className="text-xs text-gray-800 py-1 border-b border-gray-100 font-medium">
                  {p.village || 'N/A'} &mdash; {p.area} {p.area_unit} &mdash; {p.irrigation_status}
                </div>
              ))}
              {(!farmer?.land_parcels || farmer.land_parcels.length === 0) && (
                <div className="text-xs text-gray-400">No parcels</div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Note */}
      <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-md border border-gray-200">
        Changes highlighted above are auto-detected by comparing the current farmer profile against the previous cycle snapshot.
        For enhancement workflows, stronger validation applies to changed items.
      </div>
    </div>
  );
};

export default CultivationChanges;
